import React, {HtmlHTMLAttributes, ReactNode, useRef, useEffect, useMemo} from 'react';
import classnames from 'classnames';
import {Card, Avatar, createMuiTheme, makeStyles, Theme, Typography} from '@material-ui/core';
import {ThemeProvider} from '@material-ui/styles';
import LockIcon from '@material-ui/icons/Lock';
import {StaticContext} from 'react-router';
import {useHistory} from 'react-router-dom';
import {useCheckAuth} from 'ra-core';
import LoginForm from './LoginFrom';
import {Notification, defaultTheme} from 'react-admin';

interface Props {
   backgroundImage?: string;
   children: ReactNode;
   classes?: object;
   className?: string;
   staticContext?: StaticContext;
   theme: object;
}

const useStyles = makeStyles(
   (theme: Theme) => ({
      main: {
         display: 'flex',
         flexDirection: 'column',
         minHeight: '98vh',
         height: '1px',
         alignItems: 'center',
         justifyContent: 'flex-start',
         backgroundRepeat: 'no-repeat',
         backgroundSize: 'cover',
         backgroundImage: 'radial-gradient(circle at 50% 14em, #313264 0%, #00023b 60%, #00023b 100%)'
      },
      card: {
         minWidth: 450,
         marginTop: '6em'
      },
      avatar: {
         margin: '1em',
         display: 'flex',
         justifyContent: 'center',
         alignItems: 'center',
         flexDirection: 'column'
      },
      icon: {
         backgroundColor: theme.palette.secondary[500]
      }
   }),
   {name: 'RaLogin'}
);

const Login: React.FunctionComponent<Props & HtmlHTMLAttributes<HTMLDivElement>> = (props) => {
   const {theme, classes: classesOverride, className, children, staticContext, backgroundImage, ...rest} = props;
   const containerRef = useRef<HTMLDivElement>();
   const classes = useStyles(props);
   const muiTheme = useMemo(() => createMuiTheme(theme), [theme]);
   let backgroundImageLoaded = false;
   const checkAuth = useCheckAuth();
   const history = useHistory();
   useEffect(() => {
      checkAuth({}, false)
         .then(() => {
            // already authenticated, redirect to the home page
            history.push('/');
         })
         .catch(() => {
            // not authenticated, stay on the login page
         });
   }, [checkAuth, history]);

   const updateBackgroundImage = () => {
      if (!backgroundImageLoaded && containerRef.current) {
         containerRef.current.style.backgroundImage = `url(${backgroundImage})`;
         backgroundImageLoaded = true;
      }
   };

   // Load background image asynchronously to speed up time to interactive
   const lazyLoadBackgroundImage = () => {
      if (backgroundImage) {
         const img = new Image();
         img.onload = updateBackgroundImage;
         img.src = backgroundImage;
      }
   };

   useEffect(() => {
      if (!backgroundImageLoaded) {
         lazyLoadBackgroundImage();
      }
   });

   return (
      <ThemeProvider theme={muiTheme}>
         <div className={classnames(classes.main, className)} {...rest} ref={containerRef as any}>
            <Card className={classes.card}>
               <div className={classes.avatar}>
                  <Avatar className={classes.icon}>
                     <LockIcon />
                  </Avatar>
                  <Typography component="h1" variant="h5">
                     Login
                  </Typography>
               </div>
               {children}
            </Card>
            <Notification />
         </div>
      </ThemeProvider>
   );
};

// Login.propTypes = {
//   backgroundImage: PropTypes.string,
//   children: PropTypes.node,
//   classes: PropTypes.object,
//   className: PropTypes.string,
//   theme: PropTypes.object,
//   staticContext: PropTypes.object,
// };

Login.defaultProps = {
   theme: defaultTheme,
   children: <LoginForm />
};

export default Login;
